import { Hourglass } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { Card } from "@/components/ui/card";

import { Avatar } from "../contacts/Avatar";
import { SimpleList } from "../simple-list/SimpleList";
import type { Contact, Tag, Task } from "../types";
import {
  DEFAULT_STALE_DAYS,
  PARTNER_STALE_DAYS,
  getStaleContacts,
  type StaleContact,
  type StaleSection,
} from "./staleContacts";

// Fetch everything explicitly: the default page size (25) would silently drop contacts.
const MAX_ROWS = 1000;

const SECTIONS: { section: StaleSection; title: string }[] = [
  { section: "never", title: "Never contacted" },
  { section: "partner", title: `Partners · ${PARTNER_STALE_DAYS}+ days` },
  { section: "default", title: `Everyone else · ${DEFAULT_STALE_DAYS}+ days` },
];

export const StaleContacts = () => {
  const translate = useTranslate();
  const contacts = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: MAX_ROWS },
    sort: { field: "last_seen", order: "ASC" },
  });
  const tags = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: MAX_ROWS },
  });
  const tasks = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: MAX_ROWS },
    filter: { "done_date@is": null },
  });

  const isPending = contacts.isPending || tags.isPending || tasks.isPending;
  const isTruncated =
    (contacts.total ?? 0) > (contacts.data?.length ?? 0) ||
    (tasks.total ?? 0) > (tasks.data?.length ?? 0);

  const stale = isPending
    ? []
    : getStaleContacts({
        contacts: contacts.data ?? [],
        tags: tags.data ?? [],
        tasks: tasks.data ?? [],
      });

  const rowsFor = (section: StaleSection): StaleContact[] =>
    stale.filter((item) => item.section === section);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <Hourglass className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.dashboard.stale_contacts", { _: "Stale Contacts" })}
        </h2>
      </div>
      <Card className="py-2 gap-2">
        {isTruncated && (
          <p className="px-4 text-sm text-destructive">
            More than {MAX_ROWS} records: some contacts may be missing here.
          </p>
        )}
        {!isPending && stale.length === 0 && (
          <p className="p-4 text-sm">Everyone has been contacted recently.</p>
        )}
        {SECTIONS.map(({ section, title }) => {
          const rows = rowsFor(section);
          if (rows.length === 0) return null;
          return (
            <div key={section}>
              <h3 className="px-4 pt-2 text-xs font-medium uppercase text-muted-foreground">
                {title}
              </h3>
              <SimpleList<Contact>
                linkType="show"
                data={rows.map((item) => item.contact)}
                total={rows.length}
                isPending={false}
                resource="contacts"
                primaryText={(contact) =>
                  `${contact.first_name} ${contact.last_name}`
                }
                secondaryText={(contact) => {
                  const days = rows.find(
                    (item) => item.contact.id === contact.id,
                  )?.daysSince;
                  return days == null
                    ? "No contact logged"
                    : `Last seen ${days} days ago`;
                }}
                leftAvatar={(contact) => <Avatar record={contact} />}
              />
            </div>
          );
        })}
      </Card>
    </div>
  );
};
